import React, { useEffect, useState } from "react";
import "./surveyAnswer.css";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
  withStyles,
} from "@material-ui/core";
import classNames from "classnames";
import FireSvgIcon from "../../../assets/ReactSvg/FireSvgIcon";
import BackIconSvg from "../../../assets/ReactSvg/BackIconSvg";
import Stepper from "./Stepper";
import { useDispatch } from "react-redux";
import {
  addTempUser,
  getDishRecommendation,
} from "../../../store/loginActions";
import { fillPreferencesTextProduct } from "../../../utils/helper";

// Custom Checkbox Styles
const CustomCheckbox = withStyles({
  root: {
    padding: "0px",
    marginRight: "9px",
    "&$checked": {
      color: "#670001",
    },
  },
  checked: {},
})(Checkbox);

// Custom Slider Styles
const PrettoSlider = withStyles({
  root: {
    color: "#E77A45",
    height: 6,
  },
  thumb: {
    height: 24,
    width: 24,
    backgroundColor: "#E77A45",
    border: "2px solid currentColor",
    marginTop: -8,
    marginLeft: -12,
    "&:focus, &:hover, &$active": {
      boxShadow: "inherit",
    },
  },
  active: {},
  valueLabel: {
    left: "calc(-50% + 4px)",
  },
  track: {
    height: 8,
    borderRadius: 4,
  },
  rail: {
    height: 8,
    borderRadius: 4,
    background: "#F2F2F3",
    opacity: 1,
  },
  mark: {
    bottom: "-7px",
    width: "1px",
    height: "9px",
    color: "#D5D1C9",
  },
})(Slider);

const questions = [
  {
    id: 1,
    type: "button",
    question: "What makes you trust a place before you visit?",
    options: [
      `The founder telling their story`,
      `A credible creator or expert recommending it`,
      `Seeing the chef and kitchen team`,
      `The restaurant or founder posting directly`,
      `Cultural authenticity and social proof`,
      `Warm hospitality`,
    ],
    tags: {
      "The founder telling their story":
        "founder-story, founding-story, work-experience, founder-experience, founder-speaking",
      "A credible creator or expert recommending it":
        "credible-recommendation, expert-pick, famous-creator, famous-page, interview",
      "Seeing the chef and kitchen team":
        "chef-appreciation, kitchen-team-appreciation, kitchen-team-shown, chef-posted, team-shown",
      "The restaurant or founder posting directly":
        "posted-by-restaurant, founder-posted, founder-shown",
      "Cultural authenticity and social proof":
        "cultural-appreciation, social-proof, brand-jacking",
      "Warm hospitality": "hospitality",
    },
    tagType: "trustTags",
  },
  {
    id: 2,
    type: "button",
    question: "What kind of food experience do you look for?",
    options: [
      `A curated special or seasonal menu`,
      `One hero dish explored in depth`,
      `A big spread of many dishes`,
      `Cocktails and drinks`,
      `A specific cuisine recommendation`,
      `A unique or immersive dine-in experience`,
    ],
    tags: {
      "A curated special or seasonal menu": "special-menu, winter-menu",
      "One hero dish explored in depth": "one-dish-spotlight",
      "A big spread of many dishes": "multiple-dishes, food-spread",
      "Cocktails and drinks": "cocktail",
      "A specific cuisine recommendation": "cuisine-recommendation",
      "A unique or immersive dine-in experience":
        "unique-experience, dine-in-experience",
    },
    tagType: "foodTags",
  },
  {
    id: 3,
    type: "button",
    question: "What do you most want to know about a dish?",
    options: [
      `How it tastes`,
      `What's in it`,
      `How it's made`,
      `Its story or origin`,
      `Just show me how it looks`,
    ],
    tags: {
      "How it tastes": "flavour-description",
      "What's in it":
        "ingredient-details, ingredient-description, ingredient-spotlight",
      "How it's made": "process-explained, custom-preparation",
      "Its story or origin": "food-history",
      "Just show me how it looks": "dish-presentation, dish-images",
    },
    tagType: "foodTags",
  },
  {
    id: 4,
    type: "button",
    question: "What do you like to see in a food video?",
    options: [
      `The cooking process`,
      `Beautiful final plated dishes`,
      `The venue, inside and out`,
      `Real people tasting and reacting`,
      `Ingredients and sourcing`,
      `Packaging and takeaway`,
    ],
    tags: {
      "The cooking process": "dish-preparation, kitchen-shown",
      "Beautiful final plated dishes": "final-dishes",
      "The venue, inside and out": "exterior-shown, interiors-shown",
      "Real people tasting and reacting":
        "tasting, tasting-reaction, people-eating",
      "Ingredients and sourcing": "ingredients-shown, vendors-shown",
      "Packaging and takeaway": "packaging",
    },
    tagType: "contentTags",
  },
  {
    id: 5,
    type: "button",
    question: "Which video style do you enjoy most?",
    options: [
      `Aesthetic food shots set to music`,
      `Honest review or blogger-style`,
      `Fun and entertaining`,
      `Founder or chef speaking to camera`,
      `Conversation or interview`,
      `Deals and offers`,
    ],
    tags: {
      "Aesthetic food shots set to music": "music-food",
      "Honest review or blogger-style": "food-review, food-blog",
      "Fun and entertaining": "fun-video",
      "Founder or chef speaking to camera": "founder-speaking, chef-speaking",
      "Conversation or interview": "interview",
      "Deals and offers": "offer",
    },
    tagType: "contentTags",
  },
  {
    id: 6,
    type: "button",
    question: "Which of these do you love eating?",
    options: [
      `Punjabi and North Indian (kulcha, chole, lassi, kebabs, butter chicken)`,
      `South Indian (thalis, appam, parotta, sadhya)`,
      `Regional Indian (Bengali, Awadhi, Sindhi, Champaran, Goan)`,
      `Himalayan and Pan-Asian (momos, thukpa, ramen)`,
      `Cafe and coffee (croissants, bagels, V60, matcha)`,
      `Bakes and desserts (cakes, tarts, cookies, mishti doi)`,
      `Chaat and street food (gol gappa, raj kachori, samosa chaat)`,
      `Cocktails and mocktails`,
    ],
    tags: {
      "Punjabi and North Indian (kulcha, chole, lassi, kebabs, butter chicken)":
        "north-indian",
      "Kerala and South Indian (thalis, appam, parotta, sadhya)":
        "south-indian",
      "Regional Indian (Bengali, Awadhi, Sindhi, Champaran, Goan)":
        "regional-indian",
      "Himalayan and Pan-Asian (momos, thukpa, ramen)": "himalayan,asian",
      "Cafe and coffee (croissants, bagels, V60, matcha)": "coffee,bakery",
      "Bakes and desserts (cakes, tarts, cookies, mishti doi)":
        "bakery,dessert",
      "Chaat and street food (gol gappa, raj kachori, samosa chaat)": "chaat",
      "Cocktails and mocktails": "cocktails,beverages",
    },
    tagType: "cuisineTags",
  },
];


// Dynamic Questions
// const questions = [
//   {
//     id: 1,
//     type: "checkbox",
//     question: "What's your food philosophy?",
//     singleSelect: true,
//     options: [
//       `Team Green Only - "Paneer is my protein, dal is my soul food"`,
//       `Team Meat Lovers - "If it doesn't have chicken/mutton, is it even a meal?"`,
//       `The Diplomat - "Why choose sides when you can have butter chicken AND palak paneer?"`,
//       `The Chameleon - "I go with the flow... and whatever's in the fridge"`,
//     ],
//   },
//   {
//     id: 2,
//     type: "slider",
//     question: `Your relationship with "healthy" food?`,
//     options: [
//       `Comfort Seeker - "Today I need food that hugs my soul"`,
//       `YOLO Mode Activated - "Health? What's that? Pass the ghee! 🧈"`,
//       `Balanced Foodie - "Salad for lunch, pakoras for evening tea"`,
//       `Wellness Warrior - "Quinoa biryani? Sign me up! 💪"`,
//     ],
//   },
//   {
//     id: 3,
//     type: "slider",
//     question: `How "authentic" should your food be?`,
//     options: [
//       `Fusion Fanatic - "Give me that butter chicken pizza and tandoori momos!"`,
//       `Open Explorer - "Tasty is tasty, whether it's traditional or trendy"`,
//       `Selective Traditionalist - "Fusion is fine, but don't mess with my biryani basics"`,
//       `Purist - "If my dadi wouldn't recognize it, I'm not eating it"`,
//     ],
//   },
//   {
//     id: 4,
//     type: "button",
//     question: "Your appetite setting? 🍽️",
//     options: [
//       `Snack Attack - "Small bites, big flavors - think chaat portions"`,
//       `Goldilocks Zone - "Just right - one proper roti, one sabzi, perfectly balanced"`,
//       `Feast Beast - "Full thali or go home! I eat like it's a wedding"`,
//       `Mood-Dependent - "Sometimes tapas, sometimes buffet - depends on my vibe"`,
//     ],
//   },
//   {
//     id: 5,
//     type: "slider",
//     question: "How much of a flavor fanatic are you? 👅",
//     options: [
//       `Flavor? What Flavor? - "I eat for fuel, taste is whatever 🤷‍♂️"`,
//       `Casual Taster - "I notice good food, but won't cry over bland dal"`,
//       `Flavor Appreciator - "Good taste matters, but so do other things like price and convenience"`,
//       `Taste Bud Champion - "Life's too short for boring food - flavor is everything!"`,
//       `Flavor Obsessed - "I will travel across the city for the perfect pani puri 🚗💨"`,
//     ],
//   },
// ];

const SurveyAnswer = ({
  handleSwipeNextClick,
  isActive,
  handleSubmitAnswer,
  setFoodChoiceState,
  onGeoLocationClickHandler,
  handleCancel,
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);

  // Centralized answers state
  const [answers, setAnswers] = useState({});

  // Initialize checkbox structure
  useEffect(() => {
    onGeoLocationClickHandler();
    const initial = {};
    questions.forEach((q, i) => {
      if (q.type === "checkbox") {
        const obj = {};
        q.options.forEach((opt) => (obj[opt] = false));
        initial[i] = obj;
      } else if (q.type === "button") {
        initial[i] = [];
      } else if (q.type === "slider") {
        initial[i] = 2;
      }
    });
    setAnswers(initial);
  }, []);

  // Toggle answer based on type
  const toggleAnswer = (index, option, type, singleSelect = false) => {
    setAnswers((prev) => {
      const updated = { ...prev };
      const current = prev[index];

      if (type === "checkbox") {
        if (singleSelect) {
          // 🚀 Reset others if single-select
          updated[index] = {};
          questions[index].options.forEach((opt) => {
            updated[index][opt] = opt === option ? !current[option] : false;
          });
        } else {
          updated[index] = {
            ...current,
            [option]: !current[option],
          };
        }
      } else if (type === "button") {
        updated[index] = current.includes(option)
          ? current.filter((v) => v !== option)
          : [...current, option];
      }
      return updated;
    });
  };

  const setSliderAnswer = (index, value) => {
    setAnswers((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  function jsonToSentences(data) {
    // Dictionary of default options for each question, can be customized
    const defaultOptions = {
      "What's your food philosophy?": [
        `Team Green Only - "Paneer is my protein, dal is my soul food"`,
        `Team Meat Lovers - "If it doesn't have chicken/mutton, is it even a meal?"`,
        `The Diplomat - "Why choose sides when you can have butter chicken AND palak paneer?"`,
        `The Chameleon - "I go with the flow... and whatever's in the fridge"`,
      ],
      'Your relationship with "healthy" food?': [
        `Comfort Seeker - "Today I need food that hugs my soul"`,
        `YOLO Mode Activated - "Health? What's that? Pass the ghee! 🧈"`,
        `Balanced Foodie - "Salad for lunch, pakoras for evening tea"`,
        `Wellness Warrior - "Quinoa biryani? Sign me up! 💪"`,
      ],
      'How "authentic" should your food be?': [
        `Fusion Fanatic - "Give me that butter chicken pizza and tandoori momos!"`,
        `Open Explorer - "Tasty is tasty, whether it's traditional or trendy"`,
        `Selective Traditionalist - "Fusion is fine, but don't mess with my biryani basics"`,
        `Purist - "If my dadi wouldn't recognize it, I'm not eating it"`,
      ],
      "Your appetite setting? 🍽️": [
        `Snack Attack - "Small bites, big flavors - think chaat portions"`,
        `Goldilocks Zone - "Just right - one proper roti, one sabzi, perfectly balanced"`,
        `Feast Beast - "Full thali or go home! I eat like it's a wedding"`,
        `Mood-Dependent - "Sometimes tapas, sometimes buffet - depends on my vibe"`,
      ],
      "How much of a flavor fanatic are you? 👅": [
        `Flavor? What Flavor? - "I eat for fuel, taste is whatever 🤷‍♂️"`,
        `Casual Taster - "I notice good food, but won't cry over bland dal"`,
        `Flavor Appreciator - "Good taste matters, but so do other things like price and convenience"`,
        `Taste Bud Champion - "Life's too short for boring food - flavor is everything!"`,
        `Flavor Obsessed - "I will travel across the city for the perfect pani puri 🚗💨"`,
      ],
    };

    // Mapping for scale/intensity type answers, like spiciness
    const mapping = {
      "How spicy do you like your food?": [
        "Mild and creamy",
        "Medium spiced",
        "Hot and spicy",
        "Very spicy",
      ],
    };

    const sliderQuestions = [
      'Your relationship with "healthy" food?',
      'How "authentic" should your food be?',
      "How much of a flavor fanatic are you? 👅",
      "How spicy do you like your food?",
    ];

    let sentences = data.map(({ question, answer }) => {
      const options = defaultOptions[question];
      let userSelection = [];

      if (typeof answer === "object" && !Array.isArray(answer)) {
        // Checkbox style answer { option: boolean }
        userSelection = Object.keys(answer).filter((o) => answer[o]);
      } else if (Array.isArray(answer)) {
        // Multiple selections (like multi-checkbox or button group)
        userSelection = answer;
      } else if (
        typeof answer === "number" &&
        sliderQuestions.includes(question)
      ) {
        // For slider answers, map number (1-based) to option
        userSelection = [options[answer - 1]];
      }

      return (
        `"${question}" Options: ${options ? options.join(" | ") : ""}\n` +
        `User Selection: ${userSelection
          .map((ans) => `✅ ${ans}`)
          .join(" | ")}\n`
      );
    });

    // Combine all sentences into a single text block
    return sentences.join("\n");
  }

  const isStepAnswered = (index) => {
    const current = answers[index];
    if (questions[index].type === "checkbox") {
      return Object.values(current || {}).some(Boolean);
    }
    if (questions[index].type === "button") {
      return (current || []).length > 0;
    }
    if (questions[index].type === "slider") {
      // If slider always has a default value, treat any value as answered
      return current !== null && current !== undefined;
    }
    return false;
  };

  const getFoodChoice = (answers) => {
    const foodPhilosophy = answers.find(
      (item) => item.question === "What's your food philosophy?"
    );

    if (foodPhilosophy && foodPhilosophy.answer) {
      if (
        foodPhilosophy.answer[
          `Team Green Only - "Paneer is my protein, dal is my soul food"`
        ]
      ) {
        localStorage.setItem("foodChoice", "VEG");
        setFoodChoiceState("VEG");
      }
      if (
        foodPhilosophy.answer[
          `Team Meat Lovers - "If it doesn't have chicken/mutton, is it even a meal?"`
        ]
      ) {
        localStorage.setItem("foodChoice", "NONVEG");
        setFoodChoiceState("NONVEG");
      }
    }
  };

  const handleFoodChoice = (preferences) => {
    if (preferences["Vegetarian dishes only"]) {
      setFoodChoiceState("VEG");
    } else if (preferences["Non-vegetarian dishes"]) {
      setFoodChoiceState("NONVEG");
    }
  };

  const handleSubmitClick = () => {
    handleFoodChoice(answers[0]);
    let tags = {
      trustTags: "",
      foodTags: "",
      contentTags: "",
      cuisineTags: "",
    };
    questions.map((q, i) => {
      switch (q.tagType) {
        case "trustTags":
          tags.trustTags = answers[i].map((answer) => q.tags[answer]).join(",");
          break;
        case "foodTags":
          tags.foodTags = answers[i].map((answer) => q.tags[answer]).join(",");
          break;
        case "contentTags":
          tags.contentTags = answers[i]
            .map((answer) => q.tags[answer])
            .join(",");
          break;
        case "cuisineTags":
          tags.cuisineTags = answers[i]
            .map((answer) => q.tags[answer])
            .join(",");
          break;
      }
      return tags;
    });
    const formattedAnswers = {
      questions: questions.map((q, i) => ({
        question: q.question,
        answer: answers[i],
      })),
      tags: tags,
    };

    const tempUser = {
      mpUserId: localStorage.getItem("mpUserId"),
      questionnaire: formattedAnswers,
    };

    const dishSelection = {
      messages: jsonToSentences(formattedAnswers.questions),
    };

    dispatch(addTempUser(tempUser));
    getFoodChoice(formattedAnswers.questions);

    const payload = {
      question: fillPreferencesTextProduct(formattedAnswers.questions),
    };
    handleSubmitAnswer(payload);
    // dispatch(getDishRecommendation(payload));
    handleSwipeNextClick(tags);
  };

  if (!isActive) {
    return <div></div>;
  }
  return (
    <div style={{ margin: " 16px", marginTop: "0px", paddingTop: "16px" }}>
      <div
        style={{ justifyContent: "end" }}
        className="d-flex align-items-center"
      >
        {/* <button
          className={`payBottomModalContainerConfirmBtn Satoshi exploreSurveyBackBtn`}
          style={{
            width: "auto",
            border: 0,
            background: "transparent",
            padding: 0,
          }}
          onClick={() => handleCancel()}
        >
          <div className="payBottomModalContainerConfirmBtnContent">Skip</div>
        </button> */}
      </div>
      <div style={{ marginBottom: 22 }}>
        <Stepper steps={questions.length} currentStep={step - 1} />
      </div>
      {questions.map((q, index) =>
        step === index + 1 ? (
          <div key={q.id}>
            <div className="exploreSurveyQuestion Satoshi">
              {q.question.split(" ").map((word, index) => (
                <span
                  key={index}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  {word}&nbsp;
                </span>
              ))}
            </div>

            {/* Type: Checkbox */}
            {q.type === "checkbox" && (
              <FormGroup className="exploreSurveyQuestionWrapper">
                {q.options.map((opt, idx) => (
                  <FormControlLabel
                    key={opt}
                    control={
                      <CustomCheckbox
                        checked={answers[index]?.[opt] || false}
                        onChange={() =>
                          toggleAnswer(index, opt, "checkbox", q.singleSelect)
                        }
                      />
                    }
                    label={opt}
                    className="survey-option"
                    style={{ animationDelay: `${idx * 0.15}s` }} // staggered delay
                  />
                ))}
              </FormGroup>
            )}

            {/* Type: Button */}
            {q.type === "button" && (
              <div className="flavoursBoxWrapper">
                {q.options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => toggleAnswer(index, opt, "button")}
                    className={classNames("flavourButton", {
                      flavourActive: answers[index]?.includes(opt),
                      flavourInactive: !answers[index]?.includes(opt),
                    })}
                    style={{
                      padding: "12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      animationDelay: `${i * 0.15}s`, // stagger delay
                    }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}

            {/* Type: Slider */}
            {q.type === "slider" && (
              <>
                <div className="exploreSurveyAnswerSliderWrapper">
                  <span className="exploreSurveySliderLabel Satoshi">LOW</span>
                  <PrettoSlider
                    value={answers[index] || 2}
                    onChange={(e, value) => setSliderAnswer(index, value)}
                    shiftStep={1}
                    step={1}
                    marks
                    min={1}
                    max={4}
                  />
                  <span className="exploreSurveySliderLabel Satoshi">HIGH</span>
                </div>
                <div className="uniqLabelBoxContainer">
                  <FireSvgIcon />
                  <div className="uniqLabelWrapper">
                    {q.options[answers[index] - 1]}
                  </div>
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="surveyBottomBar">
              <button
                className={`payBottomModalContainerConfirmBtn Satoshi exploreSurveyBackBtn`}
                onClick={() => setStep((prev) => Math.max(1, prev - 1))}
                disabled={step === 1}
              >
                <div className="payBottomModalContainerConfirmBtnContent">
                  Back
                </div>
              </button>
              <button
                className={`payBottomModalContainerConfirmBtn Satoshi`}
                style={{ padding: 16 }}
                disabled={!isStepAnswered(index)} // 🚀 Disable until answered
                onClick={() =>
                  step === questions.length
                    ? handleSubmitClick()
                    : setStep(step + 1)
                }
              >
                <div className="payBottomModalContainerConfirmBtnContent Satoshi">
                  {step === questions.length ? "Submit" : "Next"}
                </div>
              </button>
            </div>
          </div>
        ) : null
      )}
    </div>
  );
};

export default SurveyAnswer;
